import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiClient } from '../../api/apiContext';
import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  ChatMessageResponse,
  ChatThreadResponse,
  ChatThreadStatus,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { useLocalisedName } from '../../i18n/localisedName';
import { useChatStream } from '../../realtime/useChatStream';
import { useBranchPicker } from '../scheduling/useBranchPicker';

/**
 * The branch inbox (FR-1003, DEC-044).
 *
 * Two panes: the conversations waiting, and the one being read. Deliberately not a modal — a
 * receptionist answers messages between patients arriving, and a dialogue that has to be dismissed
 * to see the queue is a dialogue that gets left open.
 *
 * <strong>Doctor threads are not here and cannot be.</strong> The endpoint returns reception
 * threads only, so there is no filter on this screen that could reveal one — which is the promise
 * DEC-044 makes to a patient who writes to her dermatologist, kept where it cannot be undone by a
 * change to a screen.
 */
export function ChatPage() {
  const { t } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const client = useApiClient();

  const [status, setStatus] = useState<ChatThreadStatus | 'LIVE'>('LIVE');
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const live = useChatStream(clinicId, branchId);

  const inbox = useApiQuery<ChatThreadResponse[]>(
    queryKeys.chatInbox(clinicId ?? '', branchId ?? '', status),
    `/clinics/${clinicId}/chat/threads?branchId=${branchId}` +
      (status === 'LIVE' ? '' : `&status=${status}`),
    { enabled: Boolean(clinicId && branchId) },
  );

  const threads = inbox.data ?? [];
  const open = threads.find((thread) => thread.id === openThreadId) ?? null;

  const messages = useApiQuery<ChatMessageResponse[]>(
    queryKeys.chatMessages(clinicId ?? '', openThreadId ?? ''),
    `/clinics/${clinicId}/chat/threads/${openThreadId}/messages`,
    { enabled: Boolean(clinicId && openThreadId) },
  );

  const invalidates = [
    queryKeys.chatInbox(clinicId ?? '', branchId ?? '', status),
    queryKeys.chatMessages(clinicId ?? '', openThreadId ?? ''),
  ];

  const post = useApiMutation<ChatMessageResponse, { readonly body: string }>(
    (variables) => ({
      path: `/clinics/${clinicId}/chat/threads/${openThreadId}/messages`,
      options: { method: 'POST', body: variables },
    }),
    { invalidates, onSuccess: () => setDraft('') },
  );

  const close = useApiMutation<ChatThreadResponse, void>(
    () => ({
      path: `/clinics/${clinicId}/chat/threads/${openThreadId}/closure`,
      options: { method: 'POST' },
    }),
    { invalidates },
  );

  const markRead = useApiMutation<ChatThreadResponse, string>(
    (threadId) => ({
      path: `/clinics/${clinicId}/chat/threads/${threadId}/read`,
      options: { method: 'POST' },
    }),
    { invalidates },
  );

  // Opening a conversation is reading it. Done here rather than on the click so that a thread that
  // gains a message while it is on screen is marked read too, which is what a person watching it
  // would expect and what stops the badge sticking at one.
  const lastMarked = useRef<string | null>(null);
  useEffect(() => {
    const unread = open?.unread ?? 0;
    if (!open?.id || unread === 0) {
      return;
    }
    const stamp = `${open.id}:${unread}`;
    if (lastMarked.current !== stamp) {
      lastMarked.current = stamp;
      markRead.mutate(open.id);
    }
  }, [open, markRead]);

  if (!clinicId) {
    return <PageHeader title={t('nav.items.chat')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.chat')}
        description={t('chat.description')}
        actions={
          <span className={live ? 'badge badge--success' : 'badge'}>
            {live ? t('board.live') : t('board.notLive')}
          </span>
        }
      />

      <div className="filters">
        <label className="field">
          <span>{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {name(branch, branch.code)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('chat.filter')}</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as ChatThreadStatus | 'LIVE')}
          >
            <option value="LIVE">{t('chat.statuses.live')}</option>
            <option value="PENDING_STAFF">{t('chat.statuses.PENDING_STAFF')}</option>
            <option value="CLOSED">{t('chat.statuses.CLOSED')}</option>
          </select>
        </label>
      </div>

      <div className="chat">
        <section className="chat__list panel" aria-label={t('chat.threads')}>
          <AsyncSection
            query={inbox}
            isEmpty={(rows) => rows.length === 0}
            emptyTitle={t('chat.empty')}
            emptyDescription={t('chat.emptyHint')}
          >
            {(rows) => (
              <ul className="chat__threads">
                {rows.map((thread) => (
                  <li key={thread.id}>
                    <button
                      type="button"
                      className={thread.id === openThreadId ? 'chat__thread is-open' : 'chat__thread'}
                      onClick={() => setOpenThreadId(thread.id ?? null)}
                    >
                      <span className="chat__thread-name">{thread.patientName}</span>
                      <span className="muted chat__thread-preview">{thread.lastMessagePreview}</span>
                      {thread.unread ? <span className="badge">{thread.unread}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </AsyncSection>
        </section>

        <section className="chat__thread-panel panel" aria-label={t('chat.conversation')}>
          {!open ? (
            <p className="muted">{t('chat.pick')}</p>
          ) : (
            <>
              <header className="panel__heading">
                <h2>{open.patientName}</h2>
                <div className="stack stack--row">
                  <span className="muted">{t(`chat.statuses.${open.status}`)}</span>
                  {open.status !== 'CLOSED' ? (
                    <button type="button" className="button button--ghost button--compact" onClick={() => close.mutate()}>
                      {t('chat.close')}
                    </button>
                  ) : null}
                </div>
              </header>

              {/* DEC-044 asks for this to be permanently visible, on every client. */}
              <p className="notice notice--quiet">{t('chat.disclaimer')}</p>

              <AsyncSection
                query={messages}
                isEmpty={(rows) => rows.length === 0}
                emptyTitle={t('chat.noMessages')}
              >
                {(rows) => (
                  <ol className="chat__messages">
                    {rows.map((message) => (
                      <li
                        key={message.id}
                        className={
                          message.senderKind === 'PATIENT'
                            ? 'chat__message chat__message--patient'
                            : 'chat__message chat__message--staff'
                        }
                      >
                        <span className="chat__message-sender">{message.senderName}</span>
                        {message.body ? <p>{message.body}</p> : null}
                        {message.hasAttachment ? (
                          <button
                            type="button"
                            className="button button--ghost button--compact"
                            onClick={() => {
                              void openAttachment(client, clinicId, message.id ?? '');
                            }}
                          >
                            {t('chat.viewPicture')}
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                )}
              </AsyncSection>

              <form
                className="chat__composer"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (draft.trim().length > 0) {
                    post.mutate({ body: draft.trim() });
                  }
                }}
              >
                <label className="field">
                  <span className="visually-hidden">{t('chat.reply')}</span>
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder={t('chat.replyPlaceholder')}
                    rows={2}
                    maxLength={4000}
                  />
                </label>
                <button type="submit" className="button" disabled={post.isPending || !draft.trim()}>
                  {t('chat.send')}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * Fetches a five-minute link and opens it.
 *
 * The URL is asked for at the moment of the click rather than rendered into the list: a presigned
 * link sitting in the DOM of a screen left open all afternoon has expired by the time anybody
 * clicks it, and the failure looks like a broken feature rather than an expired token.
 */
async function openAttachment(
  client: ReturnType<typeof useApiClient>,
  clinicId: string,
  messageId: string,
): Promise<void> {
  const link = await client.get<{ readonly url: string }>(
    `/clinics/${clinicId}/chat/messages/${messageId}/attachment`,
  );
  window.open(link.url, '_blank', 'noopener,noreferrer');
}
